import React, { useState, useMemo } from "react";
import {
  Button,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  useGetRefundsQuery,
  useUpdateRefundStatusMutation,
} from "state/api";
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
  formatCurrency,
} from "components/shared";

// One filterable page. Status is driven server-side via ?status=; the tabs
// simply set it. Existing refund statuses: pending, approved, rejected, refunded.
const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Refunded", value: "refunded" },
];

const RefundRequests = () => {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [selectedRefund, setSelectedRefund] = useState(null);
  const [dialogAction, setDialogAction] = useState(""); // approved | rejected | refunded
  const [openDialog, setOpenDialog] = useState(false);
  const [toast, setToast] = useState(null);

  const { data, isFetching } = useGetRefundsQuery({ status, search });
  const [updateRefundStatus] = useUpdateRefundStatusMutation();

  const { rows, total } = useMemo(
    () => normalizeList(data, { page: page + 1, pageSize: rowsPerPage }),
    [data, page, rowsPerPage]
  );

  const openAction = (refund, action) => {
    setSelectedRefund(refund);
    setDialogAction(action);
    setOpenDialog(true);
  };

  const handleConfirm = async () => {
    try {
      await updateRefundStatus({
        id: selectedRefund._id,
        status: dialogAction,
      }).unwrap();
      setToast({ severity: "success", msg: `Refund ${dialogAction}.` });
    } catch (err) {
      setToast({
        severity: "error",
        msg: err?.data?.error?.message || "Action failed",
      });
    } finally {
      setOpenDialog(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "refundNumber",
        headerName: "Refund No.",
        render: (r) => r.refundNumber || r._id?.slice(-6).toUpperCase(),
      },
      {
        field: "order",
        headerName: "Order",
        render: (r) => r.order?.orderNumber || "—",
      },
      {
        field: "customer",
        headerName: "Customer",
        render: (r) => r.customer?.name || "—",
      },
      {
        field: "reason",
        headerName: "Reason",
        render: (r) => r.reason || "—",
      },
      {
        field: "amount",
        headerName: "Amount",
        align: "right",
        render: (r) => formatCurrency(r.amount),
      },
      {
        field: "status",
        headerName: "Status",
        render: (r) => <StatusChip status={r.status} />,
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        render: (r) => {
          if (r.status === "pending") {
            return (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Approve">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAction(r, "approved");
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAction(r, "rejected");
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          }
          if (r.status === "approved") {
            return (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  openAction(r, "refunded");
                }}
              >
                Mark Refunded
              </Button>
            );
          }
          return "—";
        },
      },
    ],
    []
  );

  return (
    <PageContainer
      title="Refund Requests"
      subtitle="Review and resolve customer refund requests across all statuses"
    >
      <TableToolbar
        tabs={STATUS_TABS}
        tabValue={status}
        onTabChange={(v) => {
          setStatus(v);
          setPage(0);
        }}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search by refund / order number or customer…"
      />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        getRowId={(r) => r._id}
        emptyText="No refund requests match the current filters"
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Refund Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark refund{" "}
            <strong>{selectedRefund?.refundNumber}</strong> as{" "}
            <strong>{dialogAction}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </PageContainer>
  );
};

export default RefundRequests;
